/**
 * The authoritative public authorization store.
 *
 * Everything here is what a server would hold: organizations, the roles that
 * can be held inside them, who holds what, and the link table that makes an
 * identity internal staff. Nothing in this file is derived from a request.
 *
 * Development records are flagged. None of them asserts a real-world
 * relationship between Campuzon and any named institution or business.
 */
import type {
  Invitation,
  InternalStaffLink,
  Membership,
  OrgRole,
  Organization,
  PublicPermissionRecord,
  WorkspaceDescriptor,
} from '../types/workspace'

/* ------------------------------------------------------- permission register */

/**
 * Public permissions. Adding one is a data change; no authorization code has
 * to be rewritten for a new capability to become checkable.
 *
 * Note what is NOT here: nothing that reaches another person's notes, diary,
 * messages, wellbeing records, results or balances. Those are absent from the
 * register, so no role can be composed that reaches them.
 */
export const publicPermissionRegister: PublicPermissionRecord[] = [
  /* personal */
  {
    key: 'self.profile.manage',
    label: 'Manage your own profile',
    domain: 'self',
    note: 'Your own record only. Never another account, at any role.',
  },
  {
    key: 'self.academics.view',
    label: 'See your own academic record',
    domain: 'self',
    note: 'Campuzon is not the university records system; this is what has been shared with it.',
  },
  {
    key: 'self.money.view',
    label: 'See your own ledger',
    domain: 'self',
    note: 'Derived from ledger entries. Campuzon does not hold your money.',
  },

  /* institution / campus */
  {
    key: 'campus.students.view',
    label: 'See students at your campus',
    domain: 'campus',
    note: 'Aggregate and roster only, and only within your assigned campus.',
  },
  {
    key: 'campus.academics.manage',
    label: 'Manage academic structure',
    domain: 'campus',
    note: 'Programmes, levels and faculties. Not individual grades.',
  },
  {
    key: 'campus.verification.decide',
    label: 'Confirm student affiliation',
    domain: 'campus',
    note: 'Only for your own institution. A confirmation elsewhere is unreachable.',
  },
  {
    key: 'campus.housing.oversee',
    label: 'Oversee accommodation pressure',
    domain: 'campus',
    note: 'Aggregate only. Never an individual student’s tenancy.',
  },
  {
    key: 'campus.events.manage',
    label: 'Publish institutional notices and events',
    domain: 'campus',
    note: 'Content is marked institutional and cannot be mistaken for student posts.',
  },
  {
    key: 'campus.safety.respond',
    label: 'Work the campus safety desk',
    domain: 'campus',
    note: 'Alerts raised at your campus only. Notes, diary and results are absent from an alert.',
  },
  {
    key: 'campus.analytics.view',
    label: 'See campus analytics',
    domain: 'campus',
    note: 'Aggregated. Individual students are not identifiable.',
  },

  /* provider */
  {
    key: 'provider.orders.view',
    label: 'See your organization’s orders',
    domain: 'provider',
    note: 'Your organization only. Another provider’s orders are unreachable.',
  },
  {
    key: 'provider.orders.manage',
    label: 'Work your organization’s orders',
    domain: 'provider',
    note: 'Accept, deliver, dispute. Releasing escrow remains a platform decision.',
  },
  {
    key: 'provider.catalogue.manage',
    label: 'Manage packages and listings',
    domain: 'provider',
    note: 'Publishing does not verify a listing; verification is separate.',
  },
  {
    key: 'provider.earnings.view',
    label: 'See your organization’s earnings',
    domain: 'provider',
    note: 'Derived from the ledger. Payout release is not included.',
  },
  {
    key: 'provider.members.manage',
    label: 'Invite and manage members',
    domain: 'provider',
    note: 'Cannot grant a role above your own, and cannot grant internal access.',
  },

  /* housing — a provider capability, not a separate identity domain */
  {
    key: 'housing.properties.view',
    label: 'See your properties',
    domain: 'housing',
    note: 'Properties owned by your organization only.',
  },
  {
    key: 'housing.properties.manage',
    label: 'Manage properties and beds',
    domain: 'housing',
    note: 'Marking a property inspected is a Campuzon act, not yours.',
  },
  {
    key: 'housing.applications.decide',
    label: 'Decide tenancy applications',
    domain: 'housing',
    note: 'Only what the applicant’s privacy settings released is visible.',
  },
  {
    key: 'housing.rent.view',
    label: 'See the rent ledger',
    domain: 'housing',
    note: 'Campuzon does not collect or hold rent; this is a record, not a balance.',
  },

  /* opportunity */
  {
    key: 'opportunity.listings.manage',
    label: 'Manage roles and campaigns',
    domain: 'opportunity',
    note: 'Targeting is by campus and field, never by protected characteristics.',
  },
  {
    key: 'opportunity.candidates.view',
    label: 'See candidates',
    domain: 'opportunity',
    note: 'Only fields the candidate released. Withheld fields are named, not hidden.',
  },
  {
    key: 'opportunity.candidates.decide',
    label: 'Move candidates through the pipeline',
    domain: 'opportunity',
    note: 'The candidate sees the same stage you set.',
  },

  /* community */
  {
    key: 'community.posts.moderate',
    label: 'Moderate your community',
    domain: 'community',
    note: 'Scoped to your community. It is not campus-wide moderation.',
  },
  {
    key: 'community.members.manage',
    label: 'Manage community members',
    domain: 'community',
    note: 'Membership only. It reaches no member’s private records.',
  },
  {
    key: 'community.events.manage',
    label: 'Run community events',
    domain: 'community',
    note: 'Attendance is confirmed attendance, not RSVPs.',
  },
]

export const publicPermissionKeys = publicPermissionRegister.map((item) => item.key)

/* ------------------------------------------------------------ role register */

/**
 * Public roles. Note the absence of anything named "admin" — internal
 * administration is not expressible here, by construction.
 */
export const orgRoleRegister: OrgRole[] = [
  {
    id: 'orole-student',
    name: 'Student',
    appliesTo: 'personal',
    description: 'The personal workspace every Campuzon identity owns.',
    permissions: ['self.profile.manage', 'self.academics.view', 'self.money.view'],
    workspaces: ['student'],
  },
  {
    id: 'orole-campus-manager',
    name: 'Campus Manager',
    appliesTo: 'institution',
    description: 'Runs one campus. The broadest institutional role, still campus-scoped.',
    permissions: [
      'campus.students.view',
      'campus.academics.manage',
      'campus.verification.decide',
      'campus.housing.oversee',
      'campus.events.manage',
      'campus.analytics.view',
    ],
    workspaces: ['campus'],
    canManageMembers: true,
  },
  {
    id: 'orole-academic-manager',
    name: 'Academic Manager',
    appliesTo: 'institution',
    description: 'Academic structure and affiliation confirmation. No safety or housing reach.',
    permissions: ['campus.students.view', 'campus.academics.manage', 'campus.verification.decide'],
    workspaces: ['campus'],
  },
  {
    id: 'orole-events-manager',
    name: 'Events Manager',
    appliesTo: 'institution',
    description: 'Institutional notices and events only.',
    permissions: ['campus.events.manage'],
    workspaces: ['campus'],
  },
  {
    id: 'orole-safety-officer',
    name: 'Safety Officer',
    appliesTo: 'institution',
    description: 'Works the campus safety desk. Holds no academic or analytics reach.',
    permissions: ['campus.safety.respond'],
    workspaces: ['campus'],
  },
  {
    id: 'orole-provider-owner',
    name: 'Provider Owner',
    appliesTo: 'provider',
    description: 'Owns a provider organization and everything it trades.',
    permissions: [
      'provider.orders.view',
      'provider.orders.manage',
      'provider.catalogue.manage',
      'provider.earnings.view',
      'provider.members.manage',
    ],
    workspaces: ['provider'],
    canManageMembers: true,
  },
  {
    id: 'orole-store-manager',
    name: 'Store Manager',
    appliesTo: 'provider',
    description: 'Works orders and the catalogue. Cannot see earnings or invite members.',
    permissions: ['provider.orders.view', 'provider.orders.manage', 'provider.catalogue.manage'],
    workspaces: ['provider'],
  },
  {
    id: 'orole-housing-manager',
    name: 'Housing Manager',
    appliesTo: 'provider',
    description:
      'The housing capability of a provider organization — not a separate identity domain.',
    permissions: [
      'housing.properties.view',
      'housing.properties.manage',
      'housing.applications.decide',
      'housing.rent.view',
    ],
    workspaces: ['housing'],
    canManageMembers: true,
  },
  {
    id: 'orole-opportunity-manager',
    name: 'Opportunity Manager',
    appliesTo: 'employer',
    description: 'Publishes roles and works the candidate pipeline.',
    permissions: [
      'opportunity.listings.manage',
      'opportunity.candidates.view',
      'opportunity.candidates.decide',
    ],
    workspaces: ['opportunity'],
    canManageMembers: true,
  },
  {
    id: 'orole-recruiter',
    name: 'Recruiter',
    appliesTo: 'employer',
    description: 'Reads candidates without being able to publish or close a role.',
    permissions: ['opportunity.candidates.view'],
    workspaces: ['opportunity'],
  },
  {
    id: 'orole-community-manager',
    name: 'Community Manager',
    appliesTo: 'community',
    description: 'Runs one community. Moderation here is scoped to that community.',
    permissions: [
      'community.posts.moderate',
      'community.members.manage',
      'community.events.manage',
    ],
    workspaces: ['community'],
    canManageMembers: true,
  },
  {
    id: 'orole-community-moderator',
    name: 'Community Moderator',
    appliesTo: 'community',
    description: 'Moderates content without managing membership.',
    permissions: ['community.posts.moderate'],
    workspaces: ['community'],
  },
]

/* ------------------------------------------------------- workspace catalogue */

export const workspaceCatalogue: WorkspaceDescriptor[] = [
  {
    kind: 'student',
    label: 'Student',
    purpose: 'Your own campus life — academics, housing, money, community.',
    path: '/',
    ownerKind: 'identity',
  },
  {
    kind: 'campus',
    label: 'Campus',
    purpose: 'Run an institution’s presence on Campuzon.',
    path: '/institution',
    ownerKind: 'organization',
  },
  {
    kind: 'provider',
    label: 'Provider',
    purpose: 'Trade services and knowledge on the marketplace.',
    path: '/provider',
    ownerKind: 'organization',
  },
  {
    kind: 'housing',
    label: 'Housing',
    purpose: 'Let and manage student accommodation.',
    path: '/property',
    ownerKind: 'organization',
  },
  {
    kind: 'opportunity',
    label: 'Opportunity',
    purpose: 'Recruit students and graduates.',
    path: '/employer',
    ownerKind: 'organization',
  },
  {
    kind: 'community',
    label: 'Community',
    purpose: 'Run a student community, club or society.',
    path: '/community-workspace',
    ownerKind: 'organization',
  },
]

/* ----------------------------------------------------------- organizations */

const NOT_ASSERTED =
  'Verification confirms the entity is who it says it is. It is not a quality, safety or refund guarantee.'

export const organizations: Organization[] = [
  {
    id: 'org-unilag',
    kind: 'institution',
    name: 'University of Lagos',
    legalName: 'University of Lagos',
    tenantId: 'tnt-unilag',
    institutionId: 'inst-unilag',
    campusIds: ['cmp-unilag-akoka'],
    status: 'active',
    verification: {
      state: 'verified',
      source: 'NUC register',
      checkedAt: '2026-03-02T00:00:00Z',
      doesNotAssert: NOT_ASSERTED,
    },
    capabilities: ['academics', 'verification', 'safety', 'events'],
    createdAt: '2025-11-04T00:00:00Z',
    isDemo: true,
  },
  {
    id: 'org-futa',
    kind: 'institution',
    name: 'Federal University of Technology, Akure',
    legalName: 'Federal University of Technology, Akure',
    tenantId: 'tnt-futa',
    institutionId: 'inst-futa',
    campusIds: ['cmp-futa-main'],
    status: 'active',
    verification: {
      state: 'verified',
      source: 'NUC register',
      checkedAt: '2026-02-11T00:00:00Z',
      doesNotAssert: NOT_ASSERTED,
    },
    capabilities: ['academics', 'events'],
    createdAt: '2026-02-02T00:00:00Z',
    isDemo: true,
  },
  {
    id: 'org-cedarwood',
    kind: 'provider',
    name: 'Cedarwood Living',
    legalName: 'Cedarwood Living Properties Ltd',
    tenantId: 'tnt-cedarwood',
    institutionId: null,
    campusIds: ['cmp-unilag-akoka'],
    status: 'active',
    verification: {
      state: 'verified',
      source: 'CAC registration + director identity check',
      checkedAt: '2026-04-18T00:00:00Z',
      doesNotAssert: NOT_ASSERTED,
    },
    /* Housing is a capability of a provider organization. */
    capabilities: ['housing'],
    createdAt: '2026-01-09T00:00:00Z',
    isDemo: true,
  },
  {
    id: 'org-amarachi-foods',
    kind: 'provider',
    name: 'Amarachi Foods',
    legalName: null,
    tenantId: 'tnt-amarachi-foods',
    institutionId: 'inst-unilag',
    campusIds: ['cmp-unilag-akoka'],
    status: 'active',
    verification: {
      state: 'in_review',
      source: null,
      checkedAt: null,
      doesNotAssert: NOT_ASSERTED,
    },
    capabilities: ['marketplace', 'knowledge'],
    createdAt: '2026-05-22T00:00:00Z',
    isDemo: true,
  },
  {
    id: 'org-brandreach',
    kind: 'provider',
    name: 'BrandReach NG',
    legalName: 'BrandReach Media Ltd',
    tenantId: 'tnt-brandreach',
    institutionId: null,
    campusIds: [],
    status: 'active',
    verification: {
      state: 'verified',
      source: 'CAC registration',
      checkedAt: '2026-06-01T00:00:00Z',
      doesNotAssert: NOT_ASSERTED,
    },
    capabilities: ['marketplace'],
    createdAt: '2026-03-30T00:00:00Z',
    isDemo: true,
  },
  {
    id: 'org-havazon-works',
    kind: 'employer',
    name: 'Havazon Works',
    legalName: 'Havazon Works Ltd',
    tenantId: 'tnt-havazon-works',
    institutionId: null,
    campusIds: [],
    status: 'active',
    verification: {
      state: 'verified',
      source: 'Group entity register',
      checkedAt: '2026-01-30T00:00:00Z',
      doesNotAssert: NOT_ASSERTED,
    },
    capabilities: ['recruitment'],
    createdAt: '2025-12-15T00:00:00Z',
    isDemo: true,
  },
  {
    id: 'org-eng-society',
    kind: 'community',
    name: 'Engineering Society',
    legalName: null,
    tenantId: 'tnt-unilag',
    institutionId: 'inst-unilag',
    campusIds: ['cmp-unilag-akoka'],
    status: 'active',
    verification: {
      state: 'unverified',
      source: null,
      checkedAt: null,
      doesNotAssert:
        'Student-run. Not checked against the institution’s register, and not an official body.',
    },
    capabilities: ['community'],
    createdAt: '2026-04-02T00:00:00Z',
    isDemo: true,
  },
]

/* -------------------------------------------------------------- memberships */

/**
 * Seeded memberships. Amarachi deliberately holds three: the point of the
 * architecture is that one identity carries several authorised participations
 * without a second account, a second password or a second session.
 *
 * Kelechi's revoked membership is kept rather than deleted — it is the only
 * record of what that account could once reach.
 */
export const memberships: Membership[] = [
  {
    id: 'mem-amarachi-personal',
    identityId: 'CAMP-8H2K41QD',
    organizationId: null,
    orgRoleId: 'orole-student',
    status: 'active',
    scope: { level: 'campus', tenantId: 'tnt-unilag', institutionId: 'inst-unilag', campusId: 'cmp-unilag-akoka' },
    invitedBy: null,
    invitedAt: null,
    joinedAt: '2025-09-01T00:00:00Z',
    endedAt: null,
    statusReason: null,
  },
  {
    id: 'mem-amarachi-foods',
    identityId: 'CAMP-8H2K41QD',
    organizationId: 'org-amarachi-foods',
    orgRoleId: 'orole-provider-owner',
    status: 'active',
    scope: { level: 'tenant', tenantId: 'tnt-amarachi-foods' },
    invitedBy: null,
    invitedAt: null,
    joinedAt: '2026-05-22T00:00:00Z',
    endedAt: null,
    statusReason: null,
  },
  {
    id: 'mem-amarachi-eng-society',
    identityId: 'CAMP-8H2K41QD',
    organizationId: 'org-eng-society',
    orgRoleId: 'orole-community-manager',
    status: 'active',
    scope: { level: 'institution', tenantId: 'tnt-unilag', institutionId: 'inst-unilag' },
    invitedBy: '@chidin',
    invitedAt: '2026-04-05T00:00:00Z',
    joinedAt: '2026-04-06T00:00:00Z',
    endedAt: null,
    statusReason: null,
  },
  {
    id: 'mem-tunde-personal',
    identityId: 'CAMP-2P9WQ7XT',
    organizationId: null,
    orgRoleId: 'orole-student',
    status: 'active',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    invitedBy: null,
    invitedAt: null,
    joinedAt: '2026-07-30T00:00:00Z',
    endedAt: null,
    statusReason: null,
  },
  {
    id: 'mem-kelechi-personal',
    identityId: 'CAMP-5D1LM3BV',
    organizationId: null,
    orgRoleId: 'orole-student',
    status: 'active',
    scope: { level: 'campus', tenantId: 'tnt-unilag', institutionId: 'inst-unilag', campusId: 'cmp-unilag-akoka' },
    invitedBy: null,
    invitedAt: null,
    joinedAt: '2025-10-11T00:00:00Z',
    endedAt: null,
    statusReason: null,
  },
  {
    /* Revoked, and kept. The workspace must not open; the history must remain. */
    id: 'mem-kelechi-cedarwood',
    identityId: 'CAMP-5D1LM3BV',
    organizationId: 'org-cedarwood',
    orgRoleId: 'orole-housing-manager',
    status: 'revoked',
    scope: { level: 'tenant', tenantId: 'tnt-cedarwood' },
    invitedBy: '@ops.lead',
    invitedAt: '2026-02-01T00:00:00Z',
    joinedAt: '2026-02-02T00:00:00Z',
    endedAt: '2026-08-01T00:00:00Z',
    statusReason: 'Access removed while the account is under review.',
  },
  {
    /* Suspended, not deleted — grants nothing while it stands. */
    id: 'mem-tunde-brandreach',
    identityId: 'CAMP-2P9WQ7XT',
    organizationId: 'org-brandreach',
    orgRoleId: 'orole-store-manager',
    status: 'suspended',
    scope: { level: 'tenant', tenantId: 'tnt-brandreach' },
    invitedBy: '@brandreach.ng',
    invitedAt: '2026-08-02T00:00:00Z',
    joinedAt: '2026-08-03T00:00:00Z',
    endedAt: null,
    statusReason: 'Suspended by the organization pending its own review.',
  },
]

/* -------------------------------------------------------------- invitations */

export const invitations: Invitation[] = [
  {
    id: 'inv-unilag-academic',
    organizationId: 'org-unilag',
    email: 'j.ade@unilag.edu.ng',
    orgRoleId: 'orole-academic-manager',
    scope: { level: 'campus', tenantId: 'tnt-unilag', institutionId: 'inst-unilag', campusId: 'cmp-unilag-akoka' },
    invitedBy: '@registry.unilag',
    createdAt: '2026-08-12T09:00:00Z',
    expiresAt: '2026-08-19T09:00:00Z',
    state: 'sent',
    membershipId: null,
  },
  {
    /* Expired invitations grant nothing, and are kept so the attempt is visible. */
    id: 'inv-cedarwood-lapsed',
    organizationId: 'org-cedarwood',
    email: 'ops@cedarwoodliving.ng',
    orgRoleId: 'orole-housing-manager',
    scope: { level: 'tenant', tenantId: 'tnt-cedarwood' },
    invitedBy: '@cedarwood.owner',
    createdAt: '2026-06-01T09:00:00Z',
    expiresAt: '2026-06-08T09:00:00Z',
    state: 'expired',
    membershipId: null,
  },
]

/* ------------------------------------------------- internal administration */

/**
 * The ONLY route into internal authorization. There is no public role, no
 * signup option and no request shape that can add a row here.
 *
 * The link is deliberately a separate table rather than a role on the
 * identity: an identity cannot acquire internal access by being edited.
 */
export const internalStaffLinks: InternalStaffLink[] = [
  {
    identityId: 'CAMP-8H2K41QD',
    staffUserId: 'usr-0001',
    provisionedBy: 'Bootstrap — recorded at platform creation',
    provisionedAt: '2025-09-01T00:00:00Z',
  },
]

/* -------------------------------------------------------------- accessors */

export function organizationById(id: string | null): Organization | null {
  if (!id) return null
  return organizations.find((item) => item.id === id) ?? null
}

export function orgRoleById(id: string): OrgRole | null {
  return orgRoleRegister.find((item) => item.id === id) ?? null
}

export function permissionRecord(key: string): PublicPermissionRecord | null {
  return publicPermissionRegister.find((item) => item.key === key) ?? null
}

export function workspaceDescriptor(kind: string): WorkspaceDescriptor | null {
  return workspaceCatalogue.find((item) => item.kind === kind) ?? null
}
